import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { useNavigationStore } from '@/features/navigation/store';
import {
  ASSISTANT_PANEL_KEYBOARD_STEP,
  ASSISTANT_PANEL_MIN_WIDTH,
  clampAssistantPanelWidth,
  getAssistantPanelMaxWidth,
} from './panelWidth';

const VIEWPORT_RESIZE_DEBOUNCE_MS = 150;
const KEYBOARD_STEP_MULTIPLIER_WITH_SHIFT = 4;

type DragState = {
  pointerId: number;
  startClientX: number;
  startWidth: number;
  width: number;
};

/**
 * The resize gesture of the docked assistant panel.
 *
 * During the drag the width is written straight to the DOM as the panel's
 * `--assistant-panel-width` and the handle's `aria-valuenow`; the store gets
 * one commit on pointer up. A store write per move would re-render the Shell
 * and with it the persistent service iframes. React only rewrites the style
 * property when the store value changes, and that value is exactly the one
 * the DOM already shows once the commit lands.
 */
export function useAssistantPanelResize(
  panelRef: RefObject<HTMLElement>,
  handleRef: RefObject<HTMLElement>
) {
  const setAssistantPanelWidth = useNavigationStore((state) => state.setAssistantPanelWidth);
  const resetAssistantPanelWidth = useNavigationStore((state) => state.resetAssistantPanelWidth);
  const dragRef = useRef<DragState | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(
    () => () => {
      document.documentElement.removeAttribute('data-assistant-resizing');
    },
    []
  );

  useEffect(() => {
    let timeoutId: number | undefined;

    // A stored width from a wide screen must not survive on a narrow one.
    const reclamp = () => {
      setViewportWidth(window.innerWidth);
      const { assistantPanelWidth, setAssistantPanelWidth: commit } = useNavigationStore.getState();
      commit(assistantPanelWidth);
    };

    const handleResize = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(reclamp, VIEWPORT_RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const applyWidth = (width: number) => {
    panelRef.current?.style.setProperty('--assistant-panel-width', `${width}px`);
    handleRef.current?.setAttribute('aria-valuenow', String(width));
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    // Without this the browser starts a text selection that follows the drag across the page.
    event.preventDefault();
    document.documentElement.setAttribute('data-assistant-resizing', 'true');

    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startWidth: useNavigationStore.getState().assistantPanelWidth,
      width: useNavigationStore.getState().assistantPanelWidth,
    };

    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    panelRef.current?.setAttribute('data-resizing', 'true');
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    const width = clampAssistantPanelWidth(
      drag.startWidth + (event.clientX - drag.startClientX),
      window.innerWidth
    );
    drag.width = width;
    applyWidth(width);
  };

  const finishDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }

    if (
      typeof event.currentTarget.hasPointerCapture === 'function' &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panelRef.current?.removeAttribute('data-resizing');
    document.documentElement.removeAttribute('data-assistant-resizing');
    dragRef.current = null;
    setAssistantPanelWidth(drag.width);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const current = useNavigationStore.getState().assistantPanelWidth;
    const step = event.shiftKey
      ? ASSISTANT_PANEL_KEYBOARD_STEP * KEYBOARD_STEP_MULTIPLIER_WITH_SHIFT
      : ASSISTANT_PANEL_KEYBOARD_STEP;

    switch (event.key) {
      case 'ArrowRight':
        setAssistantPanelWidth(current + step);
        break;
      case 'ArrowLeft':
        setAssistantPanelWidth(current - step);
        break;
      case 'Home':
        setAssistantPanelWidth(ASSISTANT_PANEL_MIN_WIDTH);
        break;
      case 'End':
        setAssistantPanelWidth(getAssistantPanelMaxWidth(window.innerWidth));
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  return {
    isMobile: viewportWidth <= 980,
    maxWidth: getAssistantPanelMaxWidth(viewportWidth),
    handleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: finishDrag,
      onPointerCancel: finishDrag,
      onLostPointerCapture: finishDrag,
      onDoubleClick: resetAssistantPanelWidth,
      onKeyDown: handleKeyDown,
    },
  };
}
